import React from 'react';
import { Link } from 'react-router-dom';
import {
  Scan, HeartPulse, Activity, ChevronRight, Phone,
  Check, Monitor
} from 'lucide-react';

function Services() {
  return (
    <main>
      {/* ===== PAGE HEADER ===== */}
      <section className="page-header">
        <div className="container">
          <div className="section-label" style={{ justifyContent: 'center' }}>What We Offer</div>
          <h1 className="section-title fade-in">Portable Diagnostic Services</h1>
          <p className="section-subtitle fade-in fade-in-delay-1" style={{ margin: '0 auto' }}>
            Advanced portable imaging solutions delivered to your facility with precision,
            speed, and compassionate care.
          </p>
        </div>
      </section>

      {/* ===== SERVICES DETAIL ===== */}
      <section className="services-section" style={{ paddingTop: 0 }}>
        <div className="container">
          <div className="services-grid">
            <div className="service-card">
              <div className="service-icon">
                <Scan />
              </div>
              <h3>Mobile X-Ray</h3>
              <p>
                Our mobile X-ray service brings hospital-grade radiographic imaging
                directly to your facility. Using the PowerMax 1260 system with
                120kV and 15-60mA capability, we deliver exceptional image clarity
                with reduced exposure times.
              </p>
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Chest, extremity & abdominal X-rays</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Minimal patient motion artifacts</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Digital image delivery</span>
                </div>
              </div>
              <span className="service-tag">Digital Imaging</span>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <HeartPulse />
              </div>
              <h3>Portable Ultrasound</h3>
              <p>
                Non-invasive ultrasound diagnostics performed at your location.
                Real-time imaging capabilities allow immediate visualization
                for rapid assessment and informed clinical decisions without
                patient transport.
              </p>
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Abdominal & pelvic imaging</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Vascular assessment</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Soft tissue evaluation</span>
                </div>
              </div>
              <span className="service-tag">Non-Invasive</span>
            </div>

            <div className="service-card">
              <div className="service-icon">
                <Activity />
              </div>
              <h3>EKG / ECG</h3>
              <p>
                Comprehensive electrocardiogram testing for cardiac health monitoring.
                Our portable EKG equipment enables rapid heart rhythm assessment
                and early detection of cardiac conditions right at your facility.
              </p>
              <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>12-lead ECG recording</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Arrhythmia detection</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem' }}>
                  <Check size={16} style={{ color: 'var(--blue-primary)', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text-secondary)' }}>Pre-operative cardiac screening</span>
                </div>
              </div>
              <span className="service-tag">Cardiac Care</span>
            </div>
          </div>
        </div>
      </section>

      {/* ===== EQUIPMENT ===== */}
      <section className="equipment-section">
        <div className="container">
          <div className="equipment-card">
            <div>
              <div className="section-label">Our Equipment</div>
              <h3>PowerMax 1260 Digital System</h3>
              <p>
                We use the PowerMax 1260 — a 120kV, 15-60mA portable X-ray system with
                3kW across the full range. This reduces exposure times on difficult
                radiographic views, minimizing patient motion and enhancing image quality.
              </p>
              <p>
                Our Digital Upgrade package provides fast, efficient, and high-quality
                radiographic image acquisition for precise diagnostic results with
                speed and convenience.
              </p>
            </div>

            <div className="equipment-specs">
              <div className="equipment-spec">
                <div className="spec-value">120kV</div>
                <div className="spec-label">Max Voltage</div>
              </div>
              <div className="equipment-spec">
                <div className="spec-value">3kW</div>
                <div className="spec-label">Full Range Power</div>
              </div>
              <div className="equipment-spec">
                <div className="spec-value">60mA</div>
                <div className="spec-label">Max Current</div>
              </div>
              <div className="equipment-spec">
                <div className="spec-value">
                  <Monitor size={28} style={{ color: 'var(--blue-primary)' }} />
                </div>
                <div className="spec-label">Digital Upgrade</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <div className="section-label">Schedule a Service</div>
            <h2 className="section-title">Need Portable Diagnostic Imaging?</h2>
            <p className="section-subtitle">
              Submit an exam order through our simple online system or call us directly.
              Our team is ready to help 24/7.
            </p>
            <div className="cta-actions">
              <Link to="/contact" className="btn cta-btn-dark">
                Order Exam Online <ChevronRight size={18} />
              </Link>
              <a href="tel:8186265240" className="btn cta-btn-light">
                <Phone size={18} /> (818) 626-5240
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default Services;
